import { movies } from "@/mocks/movies";
import type { MediaType, Movie } from "@/mocks/movies";

export type SortKey = "updated" | "hot" | "rating";

export interface FilterState {
  type: MediaType | "all";
  genre: string | "all";
  region: string | "all";
  year: string | "all";
}

export const defaultFilters: FilterState = {
  type: "all",
  genre: "all",
  region: "all",
  year: "all",
};

export function getMovieById(id: string | undefined): Movie | undefined {
  if (!id) return undefined;
  return movies.find((item) => item.id === id);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return `${views}`;
}

export function searchMovies(keyword: string): Movie[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return movies.filter((item) => {
    const haystack = [item.title, item.genres.join(" "), item.region, item.director, item.cast.join(" ")]
      .join(" ")
      .toLowerCase();
    return haystack.includes(key);
  });
}

export function filterMovies(list: Movie[], filters: FilterState): Movie[] {
  return list.filter((item) => {
    if (filters.type !== "all" && item.type !== filters.type) return false;
    if (filters.genre !== "all" && !item.genres.includes(filters.genre)) return false;
    if (filters.region !== "all" && item.region !== filters.region) return false;
    if (filters.year !== "all" && String(item.year) !== filters.year) return false;
    return true;
  });
}

export function sortMovies(list: Movie[], key: SortKey): Movie[] {
  const sorted = [...list];
  if (key === "rating") {
    sorted.sort((a, b) => b.rating - a.rating);
  } else if (key === "hot") {
    sorted.sort((a, b) => b.views - a.views);
  } else {
    sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
  }
  return sorted;
}

export function getRelatedMovies(target: Movie, limit = 6): Movie[] {
  const sameType = movies.filter((item) => item.id !== target.id && item.type === target.type);
  const sharedGenre = sameType.filter((item) => item.genres.some((g) => target.genres.includes(g)));
  const rest = sameType.filter((item) => !sharedGenre.includes(item));
  return [...sharedGenre, ...rest].slice(0, limit);
}

export function getFeaturedMovies(): Movie[] {
  return movies.filter((item) => item.featured);
}

export function getLatestMovies(limit?: number): Movie[] {
  const sorted = sortMovies(movies, "updated");
  return limit ? sorted.slice(0, limit) : sorted;
}

export function getHotMovies(limit?: number): Movie[] {
  const sorted = sortMovies(movies, "hot");
  return limit ? sorted.slice(0, limit) : sorted;
}